var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { AccountId, LedgerId, Transaction, TransactionId, TransactionResponse } from "@hashgraph/sdk";
import { Buffer } from "buffer";
import { convertToSignerSignature, getChainByLedgerId, isEncodable, isTransaction, METHODS } from "./Utils.js";
import { CatchAll, HWCError } from "./ErrorHelper.js";
import { DAppConnector } from "./DAppConnector.js";
import { catchError, defer, delay, filter, from, lastValueFrom, takeUntil, throwIfEmpty, timeout } from "rxjs";
const handleSignerError = async (error, signer) => {
    try {
        const existingSession = await DAppConnector.instance.checkPersistedState();
        if (existingSession) {
            await DAppConnector.instance.onSessionConnected(existingSession);
        }
        else {
            const pairing = signer.client.pairing.getAll({ active: true }).pop();
            await DAppConnector.instance.connect(signer.getLedgerId(), pairing?.topic);
        }
    }
    catch (e) {
        try {
            await DAppConnector.instance.disconnect();
        }
        finally {
            await DAppConnector.instance.connect(signer.getLedgerId());
        }
    }
    return true;
};
/**
 * Implements Hedera Signer interface.
 * https://hips.hedera.com/hip/hip-338
 */
let WCSigner = class WCSigner {
    accountId;
    client;
    topic;
    ledgerId;
    extensionMethods;
    constructor(accountId, client, topic, ledgerId = LedgerId.MAINNET, extensionMethods = []) {
        this.accountId = accountId;
        this.client = client;
        this.topic = topic;
        this.ledgerId = ledgerId;
        this.extensionMethods = extensionMethods;
        this.extensionMethods
            .filter(method => !Object.values(METHODS).includes(method))
            .forEach(method => {
            this[method] = (...args) => this.extensionMethodCall(method, args);
        });
    }
    wrappedRequest(params) {
        const cancelWithPing$ = defer(() => this.client.ping({ topic: this.topic }))
            .pipe(delay(5000), timeout(10000), catchError(async () => true), filter(error => !!error));
        return lastValueFrom(from(this.client.request(params))
            .pipe(takeUntil(cancelWithPing$), throwIfEmpty(() => new HWCError(403, "Wallet is closed or locked", {}))));
    }
    getAccountId() {
        return this.accountId;
    }
    async getAccountKey() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getAccountKey",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    getLedgerId() {
        return this.ledgerId;
    }
    async getNetwork() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getNetwork",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    async getMirrorNetwork() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getMirrorNetwork",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    async sign(messages, signOptions) {
        const result = await this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "sign",
                params: {
                    accountId: this.accountId.toString(),
                    messages: messages.map(message => Buffer.from(message).toString("base64")),
                    signOptions
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
        return Promise.resolve(result.map(r => convertToSignerSignature(r)));
    }
    async extensionMethodCall(name, args) {
        const result = await this.wrappedRequest({
            topic: this.topic,
            request: {
                method: name,
                params: {
                    args,
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
        return Promise.resolve(result);
    }
    getAccountBalance() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getAccountBalance",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    getAccountInfo() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getAccountInfo",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    getAccountRecords() {
        return this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "getAccountRecords",
                params: {
                    accountId: this.accountId.toString()
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
    }
    async signTransaction(transaction) {
        const encodedTransaction = await this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "signTransaction",
                params: {
                    accountId: this.accountId.toString(),
                    executable: Buffer.from(transaction.toBytes()).toString("base64")
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
        return Transaction.fromBytes(Buffer.from(encodedTransaction, "base64"));
    }
    async checkTransaction(transaction) {
        const transactionId = transaction.transactionId;
        if (transactionId != null &&
            transactionId.accountId != null &&
            transactionId.accountId.compare(this.accountId) !== 0) {
            throw new Error("transaction's ID constructed with a different account ID");
        }
        const nodeAccountIds = (transaction.nodeAccountIds != null ? transaction.nodeAccountIds : []).map((nodeAccountId) => nodeAccountId.toString());
        const network = Object.values(await this.getNetwork()).map((nodeAccountId) => nodeAccountId.toString());
        if (!nodeAccountIds.reduce((previous, current) => previous && network.includes(current), true)) {
            throw new Error("Transaction already set node account IDs to values not within the current network");
        }
        return Promise.resolve(transaction);
    }
    async populateTransaction(transaction) {
        transaction.setTransactionId(TransactionId.generate(this.accountId));
        const network = Object.values(await this.getNetwork()).map((nodeAccountId) => typeof nodeAccountId === "string"
            ? AccountId.fromString(nodeAccountId)
            : new AccountId(nodeAccountId));
        transaction.setNodeAccountIds(network);
        return Promise.resolve(transaction);
    }
    async call(request) {
        if (!isEncodable(request)) {
            throw new Error("Argument is not executable");
        }
        const isTransactionType = isTransaction(request);
        const result = await this.wrappedRequest({
            topic: this.topic,
            request: {
                method: "call",
                params: {
                    accountId: this.accountId.toString(),
                    executable: Buffer.from(request.toBytes()).toString("base64"),
                    isTransaction: isTransactionType
                }
            },
            chainId: getChainByLedgerId(this.ledgerId)
        });
        if (result.error) {
            throw new Error(result.error);
        }
        if (!isTransactionType) {
            // @ts-ignore
            const responseTypeName = request.constructor.name.replace(/Query$/, "");
            const output = await import("@hashgraph/sdk").then((module) => module[responseTypeName]);
            const bytes = Buffer.from(result, "base64");
            return output.fromBytes(bytes);
        }
        else {
            return TransactionResponse.fromJSON(result);
        }
    }
};
WCSigner = __decorate([
    CatchAll(handleSignerError, { retry: true, retryDelay: 1000 })
], WCSigner);
export { WCSigner };
//# sourceMappingURL=WCSigner.js.map